import * as React from 'react';
import { overviewPercent as pct, overviewGap, roundedGap } from './OverviewFormatting';
import { academicMonthTicks, schoolDay, teachingWeek } from './OverviewCalendar';
export function OverviewSpark({ values, down = false }: { values: number[]; down?: boolean }) {
  if (values.length < 2) return null;
  const lo = Math.min(...values),
    hi = Math.max(...values),
    points = values.map((v, i) => [2 + (i * 80) / (values.length - 1), 27 - ((v - lo) * 24) / (hi - lo || 1)]);
  return (
    <svg className="slo-spark" viewBox="0 0 84 30" role="img" aria-label="Recorded trend">
      <path
        d={points.map((p, i) => (i ? 'L' : 'M') + p.join(' ')).join(' ')}
        fill="none"
        stroke={down ? 'var(--red-500)' : 'var(--green-500)'}
        strokeWidth="2"
      />
      <circle
        cx={points[points.length - 1][0]}
        cy={points[points.length - 1][1]}
        r="2.5"
        fill={down ? 'var(--red-500)' : 'var(--green-500)'}
      />
    </svg>
  );
}
export function OverviewReach({
  data,
  previous,
  year,
  onSelect,
  target,
  matchedPrevious
}: {
  data: any;
  previous?: any;
  year?: any;
  onSelect: () => void;
  target?: number;
  matchedPrevious?: number;
}) {
  const ref = React.useRef<HTMLDivElement>(null),
    [width, setWidth] = React.useState(550);
  React.useEffect(() => {
    const observer = new ResizeObserver(() => setWidth(ref.current?.clientWidth || 550));
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  const start = new Date(year?.start || data.period.from).getTime(),
    end = year ? new Date(year.end).getTime() + 864e5 : new Date(data.period.to).getTime();
  const X = (at: number) => 40 + Math.max(0, Math.min(1, (at - start) / (end - start))) * (width - 54),
    Y = (rate: number) => 12 + 210 * (1 - rate / 100);
  const points = (source: any, offset = 0) => {
    let rate = 0;
    return (source?.trend || []).map(p => {
      if (p.rate != null) rate = Math.max(rate, p.rate);
      return { at: new Date(p.at).getTime() + offset, rate };
    });
  };
  const valid = (data.trend || []).some(p => p.rate != null),
    current = valid ? points(data) : [],
    last = current[current.length - 1];
  const prior = previous ? points(previous, start - new Date(previous.period.from).getTime()) : [];
  const line = (values: any[]) => values.map((p, i) => (i ? 'L' : 'M') + X(p.at) + ' ' + Y(p.rate)).join(' ');
  const previousPoint = last && prior.filter(p => p.at <= last.at).slice(-1)[0];
  const prev = matchedPrevious != null ? { rate: matchedPrevious } : previousPoint;
  const observedDay = last && schoolDay(Math.min(last.at, +new Date(data.period.to)-1), data.timezone);
  const at = year && observedDay > year.end ? year.end : observedDay;
  const week = at && teachingWeek(year, at, data.overviewSettings?.teachingTermIds);
  const pointLabel = week ? `Week ${week.current}` : at ? new Date(at+'T12:00:00Z').toLocaleDateString('en-GB',{day:'numeric',month:'short',timeZone:'UTC'}) : '';
  const tooltipX = last ? Math.min(width - 162, X(last.at) + 10) : 0,
    tooltipY = last ? Math.max(8, Y(last.rate) - 34) : 0;
  return (
    <div ref={ref} className="slo-reach">
      <button type="button" onClick={onSelect} aria-label="View students behind the reach chart">
        <svg
          width="100%"
          height="250"
          viewBox={`0 0 ${width} 250`}
          role="img"
          aria-label="Cumulative reach across the academic year"
        >
          {[0, 25, 50, 75, 100].map(v => (
            <g key={v}>
              <line x1="40" x2={width - 14} y1={Y(v)} y2={Y(v)} stroke="var(--slate-150)" />
              <text x="32" y={Y(v) + 4} textAnchor="end">
                {v}%
              </text>
            </g>
          ))}
          {academicMonthTicks(start, end).map(tick => (
            <text key={tick.at} x={X(tick.at)} y="242">
              {tick.label}
            </text>
          ))}
          {target != null && (
            <g>
              <line
                x1="40"
                x2={width - 14}
                y1={Y(target)}
                y2={Y(target)}
                stroke="var(--green-500)"
                strokeDasharray="4 4"
                strokeWidth="1.5"
              />
              <text x={width - 14} y={Y(target) - 6} textAnchor="end" fill="var(--green-700)">
                Target {target}%
              </text>
            </g>
          )}
          {!!prior.length && <path d={line(prior)} fill="none" stroke="var(--slate-300)" strokeWidth="2.5" />}
          {!!last && (
            <>
              <path
                d={`M${X(current[0].at)} ${Y(0)} ${line(current).replace(/^M/, 'L')} L${X(last.at)} ${Y(0)}Z`}
                fill="var(--slo-reach-fill)"
              />
              <path d={line(current)} fill="none" stroke="var(--blue-500)" strokeWidth="3" strokeLinejoin="round" />
              <line x1={X(last.at)} x2={X(last.at)} y1="12" y2="222" stroke="var(--slate-700)" strokeDasharray="2 3" />
              <circle
                cx={X(last.at)}
                cy={Y(last.rate)}
                r="5"
                fill="var(--blue-500)"
                stroke="var(--slate-0)"
                strokeWidth="2"
              />
              <g>
                <rect x={tooltipX} y={tooltipY} width="150" height={prev ? 44 : 28} rx="8" fill="var(--slate-900)" />
                <text className="slo-tipMain" x={tooltipX + 10} y={tooltipY + 18}>
                  {pointLabel} · {pct(last.rate)}
                </text>
                {prev && (
                  <text className="slo-tipSub" x={tooltipX + 10} y={tooltipY + 34}>
                    Last year {pct(prev.rate)} · {overviewGap(last.rate - prev.rate)}
                  </text>
                )}
              </g>
            </>
          )}
          {!last && (
            <text x={width / 2} y="120" textAnchor="middle">
              Trend unavailable · How we count
            </text>
          )}
        </svg>
      </button>
      {!last && (
        <span className="slo-chartReason" title={data.trendUnavailableReason}>
          {data.trendUnavailableReason ? 'Joining dates not recorded' : 'No dated participation records'}
        </span>
      )}
    </div>
  );
}
export function OverviewEquity({
  items,
  threshold,
  onSelect,
  onHow,
  population = 'current'
}: {
  items: any[];
  threshold: number;
  onSelect: (filter: string) => void;
  onHow: () => void;
  population?: string;
}) {
  const absent = items.filter(item => item.group.total === 0),
    known = items.filter(item => item.group.total !== 0);
  return (
    <>
      <div className="slo-equity">
        {known.map(item => (
          <button
            type="button"
            className="slo-db"
            key={item.field}
            onClick={() => (item.gap != null ? onSelect(item.filter) : onHow())}
          >
            <span className="slo-dbLabel">
              {item.label === 'Free School Meals' ? 'FSM' : item.label}
              <small>{item.group.total == null ? '' : `${item.group.total} students`}</small>
            </span>
            <span className="slo-dbTrack">
              {item.gap == null ? (
                <span className="slo-suppressedText">Fewer than 10 students — hidden</span>
              ) : (
                <>
                  <i
                    className={roundedGap(item.gap) <= -threshold ? 'slo-dbLine danger' : 'slo-dbLine'}
                    style={{ left: `${Math.min(item.group.rate, item.peers.rate)}%`, width: `${Math.abs(item.gap)}%` }}
                  />
                  <i
                    className={'slo-dbDot ' + (item.field === 'sportGender' ? 'girls' : '')}
                    style={{ left: `${item.group.rate}%` }}
                  />
                  <i
                    className={'slo-dbDot peer ' + (item.field === 'sportGender' ? 'boys' : '')}
                    style={{ left: `${item.peers.rate}%` }}
                  />
                </>
              )}
            </span>
            <b className={roundedGap(item.gap) <= -threshold ? 'slo-negative' : 'slo-gapNeutral'}>
              {item.gap == null ? '' : overviewGap(item.gap)}
            </b>
          </button>
        ))}
      </div>
      <div className="slo-dbAxis">
        {[0, 25, 50, 75, 100].map(v => (
          <span key={v}>{v}%</span>
        ))}
      </div>
      {absent.map(item => (
        <p className="slo-help" key={item.field}>
          {['gender', 'sportGender'].includes(item.field)
            ? item.peers.total > 0
              ? 'No girls in the selected roster'
              : item.unknown === 0
              ? 'No students in the selected roster'
              : population === 'archive' || population === 'historical'
              ? 'Historical gender was not recorded in this roster; the girls/boys comparison is unavailable'
              : 'Gender is not recorded for the selected students'
            : `${item.label === 'Free School Meals' ? 'FSM' : item.label} isn't recorded in your MIS export`}
          {' · '}
          <button className="sli-link" onClick={onHow}>
            How we count
          </button>
        </p>
      ))}
    </>
  );
}
export function OverviewHeatmap({
  rows,
  onSelect
}: {
  rows: any[];
  onSelect: (year: string, characteristic: string) => void;
}) {
  return (
    <div className="slo-heatWrap">
      <table className="slo-heat">
        <thead>
          <tr>
            <th></th>
            {['All', 'PP', 'FSM', 'SEND', 'Girls', 'Boys', 'Gap PP'].map(label => (
              <th key={label}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows
            .filter(row => row.key !== 'unknown' && row.cells[0]?.total !== 0)
            .map(row => {
              const all = row.cells[0],
                pp = row.cells.find(cell => cell.field === 'pupilsPremium'),
                gap = all.rate != null && pp?.rate != null ? pp.rate - all.rate : null;
              return (
                <tr key={row.key}>
                  <th>{row.label}</th>
                  {row.cells.map(cell => {
                    const t = Math.max(0, Math.min(1, ((cell.rate || 0) - 15) / 75));
                    return (
                      <td key={cell.field}>
                        <button
                          type="button"
                          disabled={cell.rate == null}
                          title={
                            cell.suppressed
                              ? 'Fewer than 10 students — hidden'
                              : cell.rate == null
                              ? 'No known students'
                              : `${row.label} · ${cell.label}`
                          }
                          className={cell.suppressed ? 'sup' : cell.rate != null && cell.rate < 35 ? 'low' : ''}
                          style={
                            cell.rate == null
                              ? {}
                              : {
                                  background: `oklch(${0.97 - t * 0.5} ${0.02 + t * 0.12} 245)`,
                                  color: cell.rate > 58 ? 'var(--slate-0)' : 'var(--slate-900)'
                                }
                          }
                          onClick={() => onSelect(row.key, cell.filter)}
                        >
                          {cell.suppressed ? '<10' : pct(cell.rate)}
                        </button>
                      </td>
                    );
                  })}
                  <td
                    className={
                      gap != null && roundedGap(gap) <= -20
                        ? 'slo-negative'
                        : gap >= 0 || !roundedGap(gap)
                        ? 'slo-gapNeutral'
                        : 'slo-gapWarning'
                    }
                  >
                    {gap == null ? '—' : overviewGap(gap, false)}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </div>
  );
}
export function OverviewGender({ rows, onSelect }: { rows: any[]; onSelect: (year: string, filter: string) => void }) {
  return (
    <div className="slo-gender">
      {rows
        .filter(row => row.key !== 'unknown' && (row.group.total !== 0 || row.peers.total !== 0))
        .map(row => (
          <div className="slo-genderRow" key={row.key}>
            <b>{row.label}</b>
            <button
              type="button"
              className="girls"
              disabled={row.group.rate == null}
              onClick={() => onSelect(row.key, 'gender:FEMALE')}
              aria-label={`${row.label} girls ${pct(row.group.rate)}`}
            >
              <i style={{ width: `${(row.group.rate || 0) / 1.4}%` }} />
              <span style={{ right: `${(row.group.rate || 0) / 1.4}%` }}>{pct(row.group.rate)}</span>
            </button>
            <button
              type="button"
              className="boys"
              disabled={row.peers.rate == null}
              onClick={() => onSelect(row.key, 'gender:MALE')}
              aria-label={`${row.label} boys ${pct(row.peers.rate)}`}
            >
              <i style={{ width: `${(row.peers.rate || 0) / 1.4}%` }} />
              <span style={{ left: `${(row.peers.rate || 0) / 1.4}%` }}>{pct(row.peers.rate)}</span>
            </button>
          </div>
        ))}
    </div>
  );
}
